import { useState, useMemo, useEffect, useRef } from "react";
import { T, Badge, Card, Btn, SectionTitle, Input, Select, StatusBadge, RiskBadge, font, mono } from '../theme.js';
import { RiskRating, KYCStatus } from '../engines/kyc-engine.js';
import { AlertStatus } from '../engines/monitoring-engine.js';
import { api } from '../api-client.js';
import { ISO_COUNTRIES, SANCTIONED_COUNTRIES, HIGH_RISK_COUNTRIES, HIGH_RISK_SIC_CODES, validateCountryCode, getCountryName } from '../utils/country-codes.js';
import FSCSPanel from './FSCSPanel.jsx';

// DD/MM/YYYY date formatting (UK standard)
const fmtDateUK = d => {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return d; // pass through if not parseable
  return dt.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
};
const fmtDateTimeUK = d => {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return d;
  return dt.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) + ' ' +
    dt.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
};

// Detect demo/seed data by registration number pattern
const isDemoData = (customer) => customer?.registrationNumber?.startsWith('DEMO');

// Style for demo/seed data values
const demoStyle = { fontStyle: 'italic', color: T.textMuted };
const DemoValue = ({ value, isDemo, children }) => {
  if (!isDemo) return children || <span>{value}</span>;
  return <span style={demoStyle}>{children || value} <span style={{ fontSize: 10, color: T.textMuted }}>(demo)</span></span>;
};

// Format API errors with field-level detail when available
function formatApiError(err) {
  if (err.status === 429) {
    return 'Rate limited — please wait a moment and try again';
  }
  if (err.details && Array.isArray(err.details) && err.details.length > 0) {
    const fieldMessages = err.details.map(d => d.field ? `${d.field}: ${d.message}` : d.message);
    return fieldMessages.join('; ');
  }
  return err.message || String(err);
}

// Inline error banner component
const ErrorBanner = ({ error, onDismiss, onRetry }) => {
  if (!error) return null;
  return (
    <div style={{ padding: '10px 14px', borderRadius: 6, background: T.redBg, border: `1px solid ${T.redBorder}`, marginBottom: 12, display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, color: T.red }}>
      <span style={{ flex: 1 }}>{error}</span>
      {onRetry && <button onClick={onRetry} style={{ background: 'none', border: 'none', color: T.blue, cursor: 'pointer', fontWeight: 600, fontSize: 12, padding: '2px 8px', whiteSpace: 'nowrap' }}>Retry</button>}
      <button onClick={onDismiss} style={{ background: 'none', border: 'none', color: T.red, cursor: 'pointer', fontWeight: 700, fontSize: 14, padding: 0, lineHeight: 1 }}>✕</button>
    </div>
  );
};

// ============================================================
// COUNTRY SELECT — Searchable ISO country code dropdown
// ============================================================

const CountrySelect = ({ label, value, onChange, placeholder }) => {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [validationError, setValidationError] = useState(null);
  const ref = useRef(null);

  const filtered = useMemo(() => {
    if (!search) return ISO_COUNTRIES;
    const q = search.toLowerCase();
    return ISO_COUNTRIES.filter(c =>
      c.code.toLowerCase().includes(q) || c.name.toLowerCase().includes(q)
    );
  }, [search]);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSelect = (code) => {
    onChange(code);
    setSearch('');
    setOpen(false);
    setValidationError(null);
  };

  const handleBlur = () => {
    setTimeout(() => {
      if (!open) return;
      // Validate typed value
      if (search && !value) {
        const v = validateCountryCode(search);
        if (v.valid) { handleSelect(v.code); }
        else if (v.correctedCode) { setValidationError(v.message); }
        else { setValidationError(v.message); }
      }
    }, 200);
  };

  const displayValue = value ? `${value} - ${getCountryName(value)}` : '';
  const isSanctioned = value && SANCTIONED_COUNTRIES.has(value);
  const isHighRisk = value && HIGH_RISK_COUNTRIES.has(value);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <label style={{ fontSize: 11, fontWeight: 600, color: T.textSecondary, display: 'block', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</label>
      <input
        type="text"
        value={open ? search : displayValue}
        onChange={e => { setSearch(e.target.value); setOpen(true); setValidationError(null); }}
        onFocus={() => { setOpen(true); setSearch(''); }}
        onBlur={handleBlur}
        placeholder={placeholder || 'Search country...'}
        style={{
          width: '100%', padding: '8px 10px', borderRadius: 6, fontSize: 13,
          border: `1px solid ${isSanctioned ? T.red : isHighRisk ? T.amber : T.border}`,
          background: T.bg, color: T.text, fontFamily: font, boxSizing: 'border-box',
        }}
      />
      {isSanctioned && <div style={{ fontSize: 10, color: T.red, marginTop: 2, fontWeight: 600 }}>SANCTIONED JURISDICTION</div>}
      {isHighRisk && <div style={{ fontSize: 10, color: T.amber, marginTop: 2, fontWeight: 600 }}>HIGH-RISK JURISDICTION — EDD required</div>}
      {validationError && <div style={{ fontSize: 10, color: T.red, marginTop: 2 }}>{validationError}</div>}
      {open && filtered.length > 0 && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
          maxHeight: 200, overflowY: 'auto', background: T.surface, border: `1px solid ${T.border}`,
          borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,0.15)', marginTop: 2,
        }}>
          {filtered.slice(0, 50).map(c => (
            <div key={c.code} onMouseDown={() => handleSelect(c.code)} style={{
              padding: '6px 10px', fontSize: 12, cursor: 'pointer', fontFamily: font,
              color: SANCTIONED_COUNTRIES.has(c.code) ? T.red : HIGH_RISK_COUNTRIES.has(c.code) ? T.amber : T.text,
              background: 'transparent',
            }}
              onMouseEnter={e => e.currentTarget.style.background = T.surfaceAlt}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <span style={{ fontWeight: 600 }}>{c.code}</span> — {c.name}
              {SANCTIONED_COUNTRIES.has(c.code) && <span style={{ fontSize: 10, marginLeft: 6, color: T.red }}>SANCTIONED</span>}
              {HIGH_RISK_COUNTRIES.has(c.code) && <span style={{ fontSize: 10, marginLeft: 6, color: T.amber }}>HIGH RISK</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Sanctioned nationality badge (inline next to individual name)
const SanctionedBadge = ({ nationality }) => {
  if (!nationality) return null;
  if (SANCTIONED_COUNTRIES.has(nationality)) return <Badge color="red">Sanctioned</Badge>;
  if (HIGH_RISK_COUNTRIES.has(nationality)) return <Badge color="amber">High Risk</Badge>;
  return null;
};

// SIC code warning badge
const SicWarningBadge = ({ sicCodes }) => {
  if (!sicCodes || !Array.isArray(sicCodes)) return null;
  for (const sic of sicCodes) {
    const desc = HIGH_RISK_SIC_CODES[String(sic).trim()];
    if (desc) return <Badge color="amber">High-Risk: {desc}</Badge>;
  }
  return null;
};

// ============================================================
// STEPPER STAGES
// ============================================================

const STEPPER_STAGES = [
  { key: 'DATA_COLLECTION', label: 'Data Collection', icon: '📋' },
  { key: 'SCREENING', label: 'Screening', icon: '🔍' },
  { key: 'RM_REVIEW', label: 'RM Review', icon: '👤' },
  { key: 'DECISION', label: 'Decision', icon: '✅' },
];

const stageIndex = (status) => {
  if (status === KYCStatus.DATA_COLLECTION) return 0;
  if (status === KYCStatus.SCREENING) return 1;
  if (status === KYCStatus.RM_REVIEW || status === KYCStatus.UNDER_REVIEW) return 2;
  // APPROVED, REJECTED, REVIEW_DUE all mean decision has been reached (or was reached)
  return 3;
};

const StepperBar = ({ status }) => {
  const current = stageIndex(status);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginBottom: 24 }}>
      {STEPPER_STAGES.map((stage, i) => {
        const isComplete = i < current;
        const isCurrent = i === current;
        const bg = isComplete ? T.green : isCurrent ? T.blue : T.surfaceAlt;
        const textColor = isComplete ? '#fff' : isCurrent ? '#fff' : T.textMuted;
        const borderColor = isComplete ? T.green : isCurrent ? T.blue : T.border;
        return (
          <div key={stage.key} style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8,
              background: bg, color: textColor,
              padding: '10px 16px', borderRadius: 24,
              border: `2px solid ${borderColor}`,
              fontSize: 13, fontWeight: isCurrent ? 700 : 500,
              fontFamily: font, whiteSpace: 'nowrap',
              minWidth: 0, flex: '0 1 auto',
            }}>
              <span style={{ fontSize: 14 }}>
                {isComplete ? '✓' : stage.icon}
              </span>
              <span>{stage.label}</span>
            </div>
            {i < STEPPER_STAGES.length - 1 && (
              <div style={{
                flex: 1, height: 2, minWidth: 12,
                background: i < current ? T.green : T.border,
              }} />
            )}
          </div>
        );
      })}
    </div>
  );
};


// ============================================================
// STAGE-SPECIFIC ACTION PANELS
// ============================================================

const OwnershipAdjustmentPanel = ({ inds, newName, newPct, adjustments, setAdjustments, onSave, onCancel, saving }) => {
  const total = inds.reduce((sum, ind) => sum + (adjustments[ind.id] ?? ind.ownershipPct ?? 0), 0) + (parseInt(newPct) || 0);
  const isValid = total <= 100;

  return (
    <Card style={{ marginBottom: 16, padding: 16, border: `2px solid ${isValid ? T.greenBorder : T.redBorder}`, background: T.surfaceAlt }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <SectionTitle>Ownership Adjustment</SectionTitle>
        <div style={{
          fontSize: 16, fontWeight: 700, padding: '4px 14px', borderRadius: 6,
          color: isValid ? T.green : T.red,
          background: isValid ? T.greenBg : T.redBg,
          border: `1px solid ${isValid ? T.greenBorder : T.redBorder}`,
        }}>
          Total: {total}%
        </div>
      </div>
      <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 12 }}>
        Adjust existing ownership percentages to make room for the new individual. Total cannot exceed 100%.
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, fontFamily: font }}>
        <thead>
          <tr style={{ borderBottom: `1px solid ${T.border}` }}>
            <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: T.textSecondary, fontSize: 11, textTransform: 'uppercase' }}>Person</th>
            <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: T.textSecondary, fontSize: 11, textTransform: 'uppercase' }}>Role</th>
            <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 600, color: T.textSecondary, fontSize: 11, textTransform: 'uppercase', width: 100 }}>Ownership %</th>
          </tr>
        </thead>
        <tbody>
          {inds.map(ind => (
            <tr key={ind.id} style={{ borderBottom: `1px solid ${T.borderLight}` }}>
              <td style={{ padding: '8px 10px', fontWeight: 500, color: T.text }}>{ind.name}</td>
              <td style={{ padding: '8px 10px', color: T.textSecondary }}>{ind.role.replace('_', ' & ')}</td>
              <td style={{ padding: '8px 10px', textAlign: 'center' }}>
                <input
                  type="number" min="0" max="100"
                  value={adjustments[ind.id] ?? ind.ownershipPct ?? 0}
                  onChange={e => setAdjustments(a => ({ ...a, [ind.id]: parseInt(e.target.value) || 0 }))}
                  style={{
                    width: 60, padding: '4px 8px', borderRadius: 4, fontSize: 13, textAlign: 'center',
                    border: `1px solid ${T.border}`, background: T.bg, color: T.text, fontFamily: font,
                  }}
                />
              </td>
            </tr>
          ))}
          <tr style={{ background: T.blueBg }}>
            <td style={{ padding: '8px 10px', fontWeight: 600, color: T.blue }}>{newName || 'New individual'}</td>
            <td style={{ padding: '8px 10px', color: T.blue }}>New</td>
            <td style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 600, color: T.blue }}>{parseInt(newPct) || 0}%</td>
          </tr>
        </tbody>
      </table>
      <div style={{ fontSize: 11, color: T.textMuted, marginTop: 10, fontStyle: 'italic' }}>
        Total ownership does not need to equal 100% — only persons with significant control (25%+) need to be recorded as UBOs
      </div>
      <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
        <Btn onClick={onSave} disabled={!isValid || saving}>{saving ? 'Saving...' : 'Save Adjustments & Add Person'}</Btn>
        <Btn variant="ghost" onClick={onCancel}>Cancel</Btn>
      </div>
    </Card>
  );
};

const UBO_ISSUE_TEXT = 'UBOs with 25%+ ownership must be identified (or confirmed none exist)';

const DataCollectionPanel = ({ customer, kyc, kycCase, refresh, currentUser }) => {
  const rawComp = kyc.checkDataCompleteness(customer.id);
  const inds = Array.from(customer.individuals.values());
  const isDemo = isDemoData(customer);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: '', dateOfBirth: '', nationality: '', role: 'DIRECTOR', ownershipPct: 0, sourceOfWealth: '' });
  const [verifying, setVerifying] = useState(false);
  const [showAdjustment, setShowAdjustment] = useState(false);
  const [adjustments, setAdjustments] = useState({});
  const [savingAdjustment, setSavingAdjustment] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [uboConfirmed, setUboConfirmed] = useState(!!customer.uboConfirmedNone);

  // Overlay local UBO checkbox state onto cached completeness
  const comp = useMemo(() => {
    if (uboConfirmed && !customer.uboConfirmedNone) {
      // User checked locally but server doesn't know yet — remove UBO issue
      const filtered = rawComp.issues.filter(iss => iss !== UBO_ISSUE_TEXT);
      return { complete: filtered.length === 0, issues: filtered };
    }
    if (!uboConfirmed && customer.uboConfirmedNone) {
      // User unchecked locally — add UBO issue back
      const issues = rawComp.issues.includes(UBO_ISSUE_TEXT) ? rawComp.issues : [...rawComp.issues, UBO_ISSUE_TEXT];
      return { complete: false, issues };
    }
    return rawComp;
  }, [rawComp, uboConfirmed, customer.uboConfirmedNone]);

  const currentTotal = inds.reduce((sum, ind) => sum + (ind.ownershipPct || 0), 0);
  const newPct = parseInt(form.ownershipPct) || 0;
  const wouldExceed = currentTotal + newPct > 100;

  const handleAdd = async () => {
    if (wouldExceed && newPct > 0) {
      setAdjustments({});
      setShowAdjustment(true);
      return;
    }
    try {
      await kyc.addIndividual(customer.id, {
        name: form.name,
        dateOfBirth: form.dateOfBirth,
        nationality: form.nationality,
        role: form.role,
        ownershipPct: Number(form.ownershipPct) || 0,
        sourceOfWealth: form.sourceOfWealth || undefined,
      });
      setForm({ name: '', dateOfBirth: '', nationality: '', role: 'DIRECTOR', ownershipPct: 0, sourceOfWealth: '' });
      setAdding(false);
      setApiError(null);
      await refresh();
    } catch (e) { setApiError(formatApiError(e)); }
  };

  const handleAdjustmentSave = async () => {
    setSavingAdjustment(true);
    try {
      for (const ind of inds) {
        if (adjustments[ind.id] !== undefined && adjustments[ind.id] !== ind.ownershipPct) {
          await kyc.updateIndividual(customer.id, ind.id, { ownershipPct: Number(adjustments[ind.id]) || 0 });
        }
      }
      await kyc.addIndividual(customer.id, {
        name: form.name,
        dateOfBirth: form.dateOfBirth,
        nationality: form.nationality,
        role: form.role,
        ownershipPct: Number(form.ownershipPct) || 0,
        sourceOfWealth: form.sourceOfWealth || undefined,
      });
      setForm({ name: '', dateOfBirth: '', nationality: '', role: 'DIRECTOR', ownershipPct: 0, sourceOfWealth: '' });
      setAdding(false);
      setShowAdjustment(false);
      setAdjustments({});
      setApiError(null);
      await refresh();
    } catch (e) { setApiError(formatApiError(e)); }
    finally { setSavingAdjustment(false); }
  };

  const startEdit = (ind) => {
    setEditingId(ind.id);
    setEditForm({
      name: ind.name || '', dateOfBirth: ind.dateOfBirth || '', nationality: ind.nationality || '',
      role: ind.role || 'DIRECTOR', ownershipPct: ind.ownershipPct || 0, sourceOfWealth: ind.sourceOfWealth || '',
    });
  };

  const handleSaveEdit = async () => {
    try {
      await kyc.updateIndividual(customer.id, editingId, {
        name: editForm.name,
        dateOfBirth: editForm.dateOfBirth,
        nationality: editForm.nationality,
        role: editForm.role,
        ownershipPct: Number(editForm.ownershipPct) || 0,
        sourceOfWealth: editForm.sourceOfWealth || undefined,
      });
      setEditingId(null);
      setApiError(null);
      await refresh();
    } catch (e) { setApiError(formatApiError(e)); }
  };

  const handleVerifyCH = async () => {
    setVerifying(true);
    try {
      await kyc.verifyCH(customer.id);
      setApiError(null);
      await refresh();
    } catch (e) { setApiError(formatApiError(e)); }
    finally { setVerifying(false); }
  };

  return (
    <Card style={{ padding: 24 }}>
      <h3 style={{ fontSize: 16, fontWeight: 700, color: T.text, margin: '0 0 16px 0', fontFamily: font }}>
        Data Collection
      </h3>
      <ErrorBanner error={apiError} onDismiss={() => setApiError(null)} onRetry={async () => { setApiError(null); try { await refresh(); } catch (e) { setApiError(formatApiError(e)); } }} />

      {/* Data completeness checklist */}
      <div style={{ marginBottom: 20, padding: 16, borderRadius: 8, background: comp.complete ? T.greenBg : T.redBg, border: `1px solid ${comp.complete ? T.greenBorder : T.redBorder}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: comp.complete ? 0 : 10 }}>
          <span style={{ fontSize: 16 }}>{comp.complete ? '✓' : '✗'}</span>
          <span style={{ fontSize: 14, fontWeight: 600, color: comp.complete ? T.green : T.red }}>
            {comp.complete ? 'All data complete — ready for screening' : `${comp.issues.length} issue${comp.issues.length === 1 ? '' : 's'} to resolve`}
          </span>
        </div>
        {!comp.complete && (
          <div style={{ display: 'grid', gap: 4 }}>
            {comp.issues.map((iss, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: T.red, padding: '4px 0' }}>
                <span style={{ fontSize: 12 }}>✗</span> {iss}
              </div>
            ))}
          </div>
        )}
        {customer.type === 'CORPORATE' && !inds.some(i => i.ownershipPct >= 25) && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, fontSize: 13, color: T.text, cursor: 'pointer' }}>
            <input type="checkbox" checked={uboConfirmed} onChange={(e) => {
              setUboConfirmed(e.target.checked);
            }} />
            Confirm: no persons hold 25%+ ownership in this entity
          </label>
        )}
      </div>

      {/* Individual / director cards */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <SectionTitle>Directors, UBOs & Signatories ({inds.length})</SectionTitle>
        <Btn small onClick={() => { setAdding(!adding); setShowAdjustment(false); }}>{adding ? 'Cancel' : '+ Add Person'}</Btn>
      </div>

      {adding && (
        <Card style={{ marginBottom: 16, background: T.surfaceAlt }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Input label="Full Name" value={form.name} onChange={v => setForm(f => ({ ...f, name: v }))} placeholder="e.g. John Smith" />
            <Input label="Date of Birth (DD/MM/YYYY)" value={form.dateOfBirth} onChange={v => setForm(f => ({ ...f, dateOfBirth: v }))} placeholder="e.g. 15/01/1980" />
            <CountrySelect label="Nationality" value={form.nationality} onChange={v => setForm(f => ({ ...f, nationality: v }))} placeholder="Search country e.g. United Kingdom" />
            <Select label="Role" value={form.role} onChange={v => setForm(f => ({ ...f, role: v }))} options={[
              { value: 'DIRECTOR', label: 'Director' }, { value: 'UBO', label: 'UBO (25%+)' },
              { value: 'DIRECTOR_UBO', label: 'Director & UBO' }, { value: 'SIGNATORY', label: 'Signatory' },
            ]} />
            <Input label="Ownership %" value={form.ownershipPct} onChange={v => setForm(f => ({ ...f, ownershipPct: v }))} placeholder="e.g. 25" />
            <Input label="Source of Wealth" value={form.sourceOfWealth} onChange={v => setForm(f => ({ ...f, sourceOfWealth: v }))} placeholder="e.g. Professional income" />
          </div>
          {!showAdjustment && (
            <div style={{ marginTop: 12 }}><Btn onClick={handleAdd}>{wouldExceed && newPct > 0 ? 'Adjust Ownership & Add' : 'Add Person'}</Btn></div>
          )}
        </Card>
      )}

      {/* Ownership adjustment panel */}
      {showAdjustment && (
        <OwnershipAdjustmentPanel
          inds={inds} newName={form.name} newPct={form.ownershipPct}
          adjustments={adjustments} setAdjustments={setAdjustments}
          onSave={handleAdjustmentSave} onCancel={() => setShowAdjustment(false)}
          saving={savingAdjustment}
        />
      )}

      {inds.length === 0 && !adding && (
        <Card style={{ textAlign: 'center', padding: 30, color: T.textMuted, fontSize: 13, marginBottom: 16 }}>
          No individuals (directors/UBOs) recorded yet.
          <div style={{ marginTop: 8 }}><Btn small onClick={() => setAdding(true)}>+ Add Person</Btn></div>
        </Card>
      )}

      {inds.map(ind => {
        const isEditing = editingId === ind.id;
        return (
          <Card key={ind.id} style={{ marginBottom: 8, padding: 14, ...(isDemo ? { borderLeft: `3px solid ${T.border}` } : {}) }}>
            {isEditing ? (
              <div>
                {(() => {
                  const othersTotal = inds.filter(i => i.id !== ind.id).reduce((sum, i) => sum + (i.ownershipPct || 0), 0);
                  const editPct = parseInt(editForm.ownershipPct) || 0;
                  const editTotal = othersTotal + editPct;
                  const available = 100 - othersTotal;
                  return (
                    <div style={{ marginBottom: 10, padding: '6px 10px', borderRadius: 6, background: T.bg, border: `1px solid ${T.border}`, fontSize: 12, display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: T.textMuted }}>Others: {othersTotal}% — <span style={{ color: available > 0 ? T.green : T.red, fontWeight: 600 }}>{available}% available</span></span>
                      {editTotal > 100 && <span style={{ color: T.red, fontWeight: 600 }}>Total {editTotal}% exceeds 100%</span>}
                    </div>
                  );
                })()}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <Input label="Full Name" value={editForm.name} onChange={v => setEditForm(f => ({ ...f, name: v }))} placeholder="e.g. John Smith" />
                  <Input label="Date of Birth (DD/MM/YYYY)" value={editForm.dateOfBirth} onChange={v => setEditForm(f => ({ ...f, dateOfBirth: v }))} placeholder="e.g. 15/01/1980" />
                  <CountrySelect label="Nationality" value={editForm.nationality} onChange={v => setEditForm(f => ({ ...f, nationality: v }))} placeholder="Search country e.g. United Kingdom" />
                  <Select label="Role" value={editForm.role} onChange={v => setEditForm(f => ({ ...f, role: v }))} options={[
                    { value: 'DIRECTOR', label: 'Director' }, { value: 'UBO', label: 'UBO (25%+)' },
                    { value: 'DIRECTOR_UBO', label: 'Director & UBO' }, { value: 'SIGNATORY', label: 'Signatory' },
                  ]} />
                  <Input label="Ownership %" value={editForm.ownershipPct} onChange={v => setEditForm(f => ({ ...f, ownershipPct: v }))} />
                  <Input label="Source of Wealth" value={editForm.sourceOfWealth} onChange={v => setEditForm(f => ({ ...f, sourceOfWealth: v }))} />
                </div>
                <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
                  <Btn small onClick={handleSaveEdit}>Save</Btn>
                  <Btn small variant="ghost" onClick={() => setEditingId(null)}>Cancel</Btn>
                </div>
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <div>
                    <DemoValue isDemo={isDemo} value={ind.name}><span style={{ fontSize: 14, fontWeight: 500, color: isDemo ? T.textMuted : T.text, fontStyle: isDemo ? 'italic' : 'normal' }}>{ind.name}</span></DemoValue>
                    <div style={{ fontSize: 12, color: T.textMuted }}>{ind.role.replace('_', ' & ')}{ind.ownershipPct > 0 ? ` · ${ind.ownershipPct}%` : ''}</div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <SanctionedBadge nationality={ind.nationality} />
                    {ind.screeningResults && ind.screeningResults.length > 0
                      ? (ind.screeningResults.some(r => r.result === 'MATCH')
                        ? <Badge color="red">Match</Badge>
                        : ind.screeningResults.some(r => r.result === 'POSSIBLE_MATCH')
                          ? <Badge color="amber">Possible</Badge>
                          : <Badge color="green">Clear</Badge>)
                      : <Badge color="gray">Not screened</Badge>}
                    <Btn small variant="ghost" onClick={() => startEdit(ind)}>Edit</Btn>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginTop: 8, fontSize: 11 }}>
                  {[['DOB', fmtDateUK(ind.dateOfBirth)], ['Nationality', ind.nationality ? `${ind.nationality} — ${getCountryName(ind.nationality)}` : '—'], ['Source of Wealth', ind.sourceOfWealth || '—'], ['ID Verified', ind.idVerificationStatus || 'Pending']].map(([l, v]) =>
                    <div key={l}>
                      <span style={{ color: T.textMuted, display: 'block' }}>{l}</span>
                      <DemoValue isDemo={isDemo} value={v}><span style={{ color: T.textSecondary }}>{v}</span></DemoValue>
                      {!v || v === '—' ? <span style={{ fontSize: 10, color: T.red }}>Missing</span> : null}
                    </div>
                  )}
                </div>
              </>
            )}
          </Card>
        );
      })}

      {/* SIC code warning badge */}
      {customer.sicCodes && customer.sicCodes.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <SicWarningBadge sicCodes={customer.sicCodes} />
        </div>
      )}

      {/* Companies House verify button */}
      {customer.registrationNumber && !customer.registrationNumber.startsWith('DEMO') && (
        <div style={{ marginTop: 16 }}>
          <Btn variant="secondary" onClick={handleVerifyCH} disabled={verifying}>
            {verifying ? 'Verifying...' : 'Verify with Companies House'}
          </Btn>
        </div>
      )}

      {/* Primary CTA */}
      <div style={{ marginTop: 24, paddingTop: 20, borderTop: `1px solid ${T.border}` }}>
        {comp.complete ? (
          <Btn onClick={async () => { try {
              if (uboConfirmed !== !!customer.uboConfirmedNone) { await kyc.confirmNoUBOs(customer.id, uboConfirmed); }
              await kyc.submitForScreening(customer.id, currentUser?.username || 'api'); setApiError(null); await refresh();
            } catch (e) { setApiError(formatApiError(e)); } }}
            style={{ padding: '12px 32px', fontSize: 15, fontWeight: 700 }}>
            Submit for Screening →
          </Btn>
        ) : (
          <div>
            <Btn disabled style={{ opacity: 0.5, cursor: 'not-allowed', padding: '12px 32px', fontSize: 15, fontWeight: 700 }}>
              Submit for Screening →
            </Btn>
            <div style={{ fontSize: 13, color: T.red, marginTop: 10 }}>
              Cannot submit — resolve the {comp.issues.length} issue{comp.issues.length === 1 ? '' : 's'} above first
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};


const ScreeningPanel = ({ customer, kyc, kycCase, refresh, currentUser }) => {
  const [screening, setScreening] = useState(false);
  const [screenError, setScreenError] = useState(null);
  const [returning, setReturning] = useState(false);

  const handleReturnToDataCollection = async () => {
    setReturning(true);
    try {
      await kyc.returnToDataCollection(customer.id, currentUser?.username || 'api', 'Screening failed — returning to fix data issues');
      await refresh();
    } catch (e) {
      setScreenError(formatApiError(e));
    } finally {
      setReturning(false);
    }
  };
  const results = kycCase.screeningResults;
  const inds = Array.from(customer.individuals.values());
  const provider = kycCase.screeningProvider || 'simulated';

  const handleRunScreening = async () => {
    setScreening(true);
    setScreenError(null);
    try {
      await kyc.runScreening(customer.id);
      await refresh();
    } catch (e) {
      setScreenError(formatApiError(e));
    } finally {
      setScreening(false);
    }
  };

  // No results yet
  if (results.length === 0) {
    return (
      <Card style={{ padding: 24, textAlign: 'center' }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, color: T.text, margin: '0 0 20px 0', fontFamily: font }}>
          Screening
        </h3>
        {screening ? (
          <div>
            <div style={{ fontSize: 32, marginBottom: 12 }}>⏳</div>
            <div style={{ fontSize: 15, fontWeight: 600, color: T.purple, marginBottom: 6 }}>
              Screening in progress{provider === 'griffin' ? ' via Griffin Verify' : ''}...
            </div>
            <div style={{ fontSize: 12, color: T.textMuted }}>
              {provider === 'griffin' ? 'Running sanctions, PEP, and adverse media checks via Griffin Verify' : 'Running simulated screening checks'}
            </div>
          </div>
        ) : (
          <div>
            <div style={{ fontSize: 32, marginBottom: 12 }}>🔍</div>
            <div style={{ fontSize: 14, color: T.textSecondary, marginBottom: 20 }}>
              Ready to screen {inds.length} individual{inds.length !== 1 ? 's' : ''} against sanctions, PEP, and adverse media databases.
            </div>
            {screenError && <div style={{ fontSize: 13, color: T.red, marginBottom: 12, padding: '8px 12px', background: T.redBg, borderRadius: 6, border: `1px solid ${T.redBorder}` }}>{screenError}</div>}
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
              <Btn onClick={handleRunScreening} style={{ padding: '12px 32px', fontSize: 15, fontWeight: 700 }}>
                Run Screening
              </Btn>
              <Btn onClick={handleReturnToDataCollection} disabled={returning}
                style={{ padding: '12px 24px', fontSize: 14, fontWeight: 600, background: T.amberBg, color: T.amber, border: `1px solid ${T.amberBorder}` }}>
                {returning ? 'Returning...' : 'Return to Data Collection'}
              </Btn>
            </div>
          </div>
        )}
      </Card>
    );
  }

  // Results available
  const checkTypes = ['SANCTIONS', 'PEP', 'ADVERSE_MEDIA'];
  return (
    <Card style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, color: T.text, margin: 0, fontFamily: font }}>
          Screening Results
        </h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Badge color={provider === 'griffin' ? 'purple' : 'gray'}>
            {provider === 'griffin' ? 'Griffin Verify' : 'Simulated'}
          </Badge>
          {kycCase.griffinVerificationUrl && (
            <span style={{ fontSize: 11, color: T.blue, fontFamily: mono }}>{kycCase.griffinVerificationUrl}</span>
          )}
        </div>
      </div>

      {/* Results table: individual × check type */}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, fontFamily: font }}>
          <thead>
            <tr style={{ background: T.surfaceAlt, borderBottom: `1px solid ${T.border}` }}>
              <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: T.textSecondary, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>Individual</th>
              {checkTypes.map(ct => (
                <th key={ct} style={{ padding: '10px 14px', textAlign: 'center', fontWeight: 600, color: T.textSecondary, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>{ct.replace('_', ' ')}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {inds.map(ind => {
              const indResults = results.filter(r => r.individualId === ind.id);
              return (
                <tr key={ind.id} style={{ borderBottom: `1px solid ${T.borderLight}` }}>
                  <td style={{ padding: '12px 14px', fontWeight: 500, color: T.text }}>
                    {ind.name}
                    <div style={{ fontSize: 11, color: T.textMuted }}>{ind.role.replace('_', ' & ')}</div>
                  </td>
                  {checkTypes.map(ct => {
                    const match = indResults.find(r => r.type === ct);
                    if (!match) return <td key={ct} style={{ padding: '12px 14px', textAlign: 'center' }}><Badge color="gray">N/A</Badge></td>;
                    const badgeColor = match.result === 'CLEAR' ? 'green' : match.result === 'MATCH' ? 'red' : 'amber';
                    return (
                      <td key={ct} style={{ padding: '12px 14px', textAlign: 'center' }}>
                        <Badge color={badgeColor}>{match.result.replace('_', ' ')}</Badge>
                        {match.details && <div style={{ fontSize: 10, color: T.textMuted, marginTop: 2 }}>{match.details}</div>}
                        {match.source === 'griffin' && <div style={{ fontSize: 10, color: T.purple, marginTop: 1 }}>via Griffin</div>}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Action buttons */}
      <div style={{ marginTop: 24, paddingTop: 20, borderTop: `1px solid ${T.border}`, display: 'flex', gap: 12 }}>
        <Btn onClick={async () => { try { await kyc.submitForRMReview(customer.id, currentUser?.username || 'api'); setScreenError(null); await refresh(); } catch (e) { setScreenError(formatApiError(e)); } }}
          style={{ padding: '12px 32px', fontSize: 15, fontWeight: 700 }}>
          Proceed to RM Review →
        </Btn>
        <Btn onClick={handleReturnToDataCollection} disabled={returning}
          style={{ padding: '12px 24px', fontSize: 14, fontWeight: 600, background: T.amberBg, color: T.amber, border: `1px solid ${T.amberBorder}` }}>
          {returning ? 'Returning...' : 'Return to Data Collection'}
        </Btn>
      </div>
    </Card>
  );
};


const RMReviewPanel = ({ customer, kyc, kycCase, monitoring, runQualityGate, extractActivityProfile, refresh, draft, setDraft, isPeriodicReview, currentUser }) => {
  const form = draft;
  const setFormRaw = setDraft;
  const setForm = (updater) => { setApiError(null); setFormRaw(updater); };
  const [override, setOverride] = useState(false);
  const [complianceOverride, setComplianceOverride] = useState(false);
  const [complianceRationale, setComplianceRationale] = useState('');
  const [goingBack, setGoingBack] = useState(null);
  const [backError, setBackError] = useState(null);
  const [apiError, setApiError] = useState(null);
  const hasMatches = kycCase.screeningResults.some(r => r.result === 'MATCH' || r.result === 'POSSIBLE_MATCH');
  const results = kycCase.screeningResults;
  const inds = Array.from(customer.individuals.values());

  const hasSanctionedCountry = useMemo(() => {
    return inds.some(ind => ind.nationality && SANCTIONED_COUNTRIES.has(ind.nationality));
  }, [inds]);

  const qr = useMemo(() => {
    const result = runQualityGate(form, { customerName: customer.legalName, riskRating: form.riskRating, hasScreeningMatches: hasMatches, individuals: inds, registeredAddress: customer.registeredAddress, sicCodes: customer.sicCodes });
    console.log('[KYC Approve Debug] recommendation:', JSON.stringify(form.recommendation),
      '| requiresOverride:', result.requiresOverride,
      '| override:', override,
      '| criticals:', result.criticals.map(c => c.check + ':' + c.warning),
      '| disabled:', (form.recommendation !== 'APPROVE' || (result.requiresOverride && !override)));
    return result;
  }, [form, customer.legalName, customer.registeredAddress, customer.sicCodes, inds, hasMatches, runQualityGate, override]);
  const ap = useMemo(() => extractActivityProfile(form.expectedActivity), [form.expectedActivity, extractActivityProfile]);
  const fmtCcy = v => v ? `£${v.toLocaleString()}` : '—';

  const submit = async (action) => {
    try {
      const payload = { ...form };
      if (action === 'approve') payload.recommendation = 'APPROVE';
      else if (action === 'reject') payload.recommendation = 'REJECT';
      else if (action === 'return') payload.recommendation = 'RETURN_FOR_INFO';
      if (hasSanctionedCountry && complianceOverride && complianceRationale) {
        payload.complianceOverride = { sanctionedCountry: true, rationale: complianceRationale, overrideBy: currentUser?.username || 'unknown', overrideAt: new Date().toISOString() };
      }
      await kyc.recordRMAssessment(customer.id, payload);
      if (action === 'approve' && ap && ap.confidence !== 'NONE') {
        try { await monitoring.registerProfile(customer.id, ap, form.riskRating); }
        catch (profileErr) { console.warn('Monitoring profile registration failed (non-blocking):', profileErr.message); }
      }
      setApiError(null);
      await refresh();
    } catch (e) { setApiError(formatApiError(e)); }
  };

  // Field quality indicators
  const fieldCheck = (field, value, minLen = 1) => {
    if (!value || value.trim().length < minLen) return { ok: false, color: T.red, icon: '✗' };
    return { ok: true, color: T.green, icon: '✓' };
  };
  const buCheck = fieldCheck('businessUnderstanding', form.businessUnderstanding, 50);
  const sowCheck = fieldCheck('sourceOfWealth', form.sourceOfWealth);
  const eaCheck = fieldCheck('expectedActivity', form.expectedActivity);
  const concernsRequired = hasMatches;
  const concernsCheck = concernsRequired ? fieldCheck('concerns', form.concerns) : { ok: true, color: T.green, icon: '✓' };
  const trivialConcerns = ['none', 'none identified', 'no concerns', 'n/a', 'na', 'nil', '-', 'not applicable'];
  const mitigantsRequired = form.concerns && form.concerns.trim().length > 0 && !trivialConcerns.includes(form.concerns.trim().toLowerCase());
  const mitigantsCheck = mitigantsRequired ? fieldCheck('mitigants', form.mitigants) : { ok: true, color: T.green, icon: '✓' };

  const prevAssessment = isPeriodicReview ? kycCase.rmAssessment : null;

  return (
    <div>
      {/* Screening summary (read-only reference) */}
      {results.length > 0 && (
        <Card style={{ padding: 16, marginBottom: 16, background: T.surfaceAlt }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: T.textSecondary, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>Screening Summary</div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {inds.map(ind => {
              const ir = results.filter(r => r.individualId === ind.id);
              const worst = ir.some(r => r.result === 'MATCH') ? 'MATCH' : ir.some(r => r.result === 'POSSIBLE_MATCH') ? 'POSSIBLE_MATCH' : 'CLEAR';
              return (
                <div key={ind.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                  <span style={{ color: T.text, fontWeight: 500 }}>{ind.name}</span>
                  <Badge color={worst === 'MATCH' ? 'red' : worst === 'POSSIBLE_MATCH' ? 'amber' : 'green'}>
                    {worst.replace('_', ' ')}
                  </Badge>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Previous assessment reference for periodic review */}
      {isPeriodicReview && prevAssessment && (
        <Card style={{ padding: 16, marginBottom: 16, background: T.surfaceAlt, borderLeft: `3px solid ${T.purple}` }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: T.purple, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>Previous Assessment — {fmtDateUK(prevAssessment.recordedAt)}</div>
          <div style={{ fontSize: 12, color: T.textSecondary, lineHeight: 1.6 }}>
            <div><strong>Business:</strong> {prevAssessment.businessUnderstanding || '—'}</div>
            <div><strong>Source of Wealth:</strong> {prevAssessment.sourceOfWealth || '—'}</div>
            <div><strong>Expected Activity:</strong> {prevAssessment.expectedActivity || '—'}</div>
            <div><strong>Risk:</strong> {prevAssessment.riskRating || '—'} · <strong>Recommendation:</strong> {prevAssessment.recommendation || '—'}</div>
          </div>
        </Card>
      )}

      {/* Sanctioned country banner */}
      {hasSanctionedCountry && (
        <div style={{ padding: '16px 20px', borderRadius: 8, background: T.redBg, border: `2px solid ${T.red}`, marginBottom: 16 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: T.red, marginBottom: 8 }}>
            SANCTIONED JURISDICTION DETECTED — Compliance override required
          </div>
          <div style={{ marginBottom: 10 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: T.red, display: 'block', marginBottom: 4 }}>Compliance override rationale</label>
            <textarea
              value={complianceRationale}
              onChange={e => setComplianceRationale(e.target.value)}
              placeholder="Provide detailed rationale for compliance override..."
              rows={3}
              style={{ width: '100%', padding: '8px 10px', borderRadius: 6, fontSize: 13, border: `1px solid ${T.red}`, background: T.bg, color: T.text, fontFamily: font, boxSizing: 'border-box', resize: 'vertical' }}
            />
          </div>
          <label style={{ fontSize: 12, color: complianceRationale.trim().length >= 20 ? T.red : T.textMuted, display: 'flex', alignItems: 'center', gap: 6, cursor: complianceRationale.trim().length >= 20 ? 'pointer' : 'not-allowed' }}>
            <input type="checkbox" checked={complianceOverride} disabled={complianceRationale.trim().length < 20}
              onChange={e => setComplianceOverride(e.target.checked)} />
            I confirm this customer may proceed despite sanctioned jurisdiction (logged to audit trail)
            {complianceRationale.trim().length < 20 && <span style={{ fontSize: 10, color: T.textMuted }}>(min 20 characters rationale required)</span>}
          </label>
        </div>
      )}

      {/* High-risk SIC code banner */}
      {customer.sicCodes && customer.sicCodes.some(sic => HIGH_RISK_SIC_CODES[String(sic).trim()]) && (
        <div style={{ padding: '12px 16px', borderRadius: 8, background: T.amberBg, border: `1px solid ${T.amberBorder}`, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 16 }}>&#9888;</span>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: T.amber }}>
              High-risk industry classification — Suggested risk: MEDIUM or above
            </div>
            <div style={{ fontSize: 12, color: T.textSecondary, marginTop: 2 }}>
              {customer.sicCodes.filter(sic => HIGH_RISK_SIC_CODES[String(sic).trim()]).map(sic => `${HIGH_RISK_SIC_CODES[String(sic).trim()]} (SIC ${sic})`).join(', ')}
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 16 }}>
        {/* Assessment form */}
        <Card style={{ padding: 24 }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: T.text, margin: '0 0 16px 0', fontFamily: font }}>
            {isPeriodicReview ? 'Periodic Review' : 'RM Assessment'}
          </h3>
          <div style={{ position: 'relative' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
              <span style={{ color: buCheck.color, fontSize: 12, fontWeight: 700 }}>{buCheck.icon}</span>
              <span style={{ fontSize: 11, color: T.textMuted }}>Min 50 characters</span>
            </div>
            <Input label="Business Understanding" value={form.businessUnderstanding} onChange={v => setForm(f => ({ ...f, businessUnderstanding: v }))} multiline placeholder="What the business does, industry, scale, key customers/suppliers..." />
          </div>

          <div style={{ position: 'relative' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
              <span style={{ color: sowCheck.color, fontSize: 12, fontWeight: 700 }}>{sowCheck.icon}</span>
            </div>
            <Input label="Source of Wealth" value={form.sourceOfWealth} onChange={v => setForm(f => ({ ...f, sourceOfWealth: v }))} multiline placeholder="How was the business funded?" />
          </div>

          <div style={{ position: 'relative' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
              <span style={{ color: eaCheck.color, fontSize: 12, fontWeight: 700 }}>{eaCheck.icon}</span>
              <span style={{ fontSize: 11, color: T.textMuted }}>Include £ amounts</span>
            </div>
            <Input label="Expected Activity" value={form.expectedActivity} onChange={v => setForm(f => ({ ...f, expectedActivity: v }))} multiline placeholder="£200k monthly turnover, ~30 inbound FPS, ~10 outbound BACS..." />
          </div>

          <div style={{ position: 'relative' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
              <span style={{ color: concernsCheck.color, fontSize: 12, fontWeight: 700 }}>{concernsCheck.icon}</span>
              {concernsRequired && <span style={{ fontSize: 11, color: T.red }}>Required — screening matches found</span>}
            </div>
            <Input label="Concerns" value={form.concerns} onChange={v => setForm(f => ({ ...f, concerns: v }))} multiline />
          </div>

          <div style={{ position: 'relative' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
              <span style={{ color: mitigantsCheck.color, fontSize: 12, fontWeight: 700 }}>{mitigantsCheck.icon}</span>
              {mitigantsRequired && <span style={{ fontSize: 11, color: T.amber }}>Required when concerns noted</span>}
            </div>
            <Input label="Mitigants" value={form.mitigants} onChange={v => setForm(f => ({ ...f, mitigants: v }))} multiline />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Select label="Recommendation" value={form.recommendation} onChange={v => setForm(f => ({ ...f, recommendation: v }))} options={[
              { value: 'APPROVE', label: 'Approve' }, { value: 'REJECT', label: 'Reject' }, { value: 'RETURN_FOR_INFO', label: 'Return for Info' },
            ]} />
            <Select label="Risk Rating" value={form.riskRating} onChange={v => setForm(f => ({ ...f, riskRating: v }))} options={Object.values(RiskRating).map(r => ({ value: r, label: r }))} />
          </div>

          <ErrorBanner error={apiError} onDismiss={() => setApiError(null)} onRetry={async () => { setApiError(null); try { await refresh(); } catch (e) { setApiError(formatApiError(e)); } }} />

          {/* Three action buttons */}
          <div style={{ marginTop: 24, paddingTop: 20, borderTop: `1px solid ${T.border}`, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <Btn onClick={() => submit('approve')}
              disabled={(form.recommendation !== 'APPROVE' || (qr.requiresOverride && !override) || (hasSanctionedCountry && !complianceOverride))}
              style={{ padding: '12px 28px', fontSize: 14, fontWeight: 700, background: T.green, borderColor: T.green }}>
              Approve Customer
            </Btn>
            <Btn onClick={() => submit('reject')}
              variant="danger"
              style={{ padding: '12px 28px', fontSize: 14, fontWeight: 700 }}>
              Reject Customer
            </Btn>
            <Btn onClick={() => submit('return')}
              variant="secondary"
              style={{ padding: '12px 28px', fontSize: 14, fontWeight: 700, background: T.amberBg, borderColor: T.amberBorder, color: T.amber }}>
              Return for More Info
            </Btn>
            {qr.requiresOverride && (
              <label style={{ fontSize: 12, color: T.red, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                <input type="checkbox" checked={override} onChange={e => setOverride(e.target.checked)} /> Override criticals (logged)
              </label>
            )}
          </div>

          {/* Back navigation buttons */}
          <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${T.border}`, display: 'flex', gap: 10, alignItems: 'center' }}>
            <span style={{ fontSize: 11, color: T.textMuted, marginRight: 4 }}>Go back:</span>
            <Btn onClick={async () => {
              setGoingBack('data'); setBackError(null);
              try { await kyc.returnToDataCollection(customer.id, currentUser?.username || 'api', 'Data issues found during review'); await refresh(); }
              catch (e) { setBackError(e.message); }
              finally { setGoingBack(null); }
            }} disabled={goingBack !== null}
              variant="secondary"
              style={{ padding: '8px 16px', fontSize: 12, background: T.amberBg, borderColor: T.amberBorder, color: T.amber }}>
              {goingBack === 'data' ? 'Returning...' : 'Return to Data Collection'}
            </Btn>
            <Btn onClick={async () => {
              setGoingBack('screening'); setBackError(null);
              try { await kyc.returnToScreening(customer.id, currentUser?.username || 'api', 'Re-screening requested'); await refresh(); }
              catch (e) { setBackError(e.message); }
              finally { setGoingBack(null); }
            }} disabled={goingBack !== null}
              variant="secondary"
              style={{ padding: '8px 16px', fontSize: 12, background: T.amberBg, borderColor: T.amberBorder, color: T.amber }}>
              {goingBack === 'screening' ? 'Returning...' : 'Return to Screening'}
            </Btn>
            {backError && <span style={{ fontSize: 12, color: T.red }}>{backError}</span>}
          </div>
        </Card>

        {/* Quality gate sidebar */}
        <div>
          <Card style={{ marginBottom: 16, padding: 14 }}>
            <SectionTitle>Quality Gate</SectionTitle>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: qr.pass ? T.green : T.red }} />
              <span style={{ fontSize: 13, fontWeight: 600, color: qr.pass ? T.green : T.red }}>
                {qr.pass ? 'Passing' : `${qr.criticals.length} critical · ${qr.warnings.length} warning`}
              </span>
            </div>
            {qr.results.length === 0
              ? <div style={{ fontSize: 12, color: T.green }}>✓ All checks passing</div>
              : qr.results.map((r, i) => (
                <div key={i} style={{
                  padding: '8px 10px', marginBottom: 4, borderRadius: 4, fontSize: 12,
                  background: r.severity === 'CRITICAL' ? T.redBg : r.severity === 'WARNING' ? T.amberBg : T.blueBg,
                  color: r.severity === 'CRITICAL' ? T.red : r.severity === 'WARNING' ? T.amber : T.blue,
                  border: `1px solid ${r.severity === 'CRITICAL' ? T.redBorder : r.severity === 'WARNING' ? T.amberBorder : T.blueBorder}`,
                }}>
                  <div style={{ fontWeight: 600, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 2 }}>{r.severity} — {r.field}</div>
                  {r.warning}
                </div>
              ))}
          </Card>

          <Card style={{ padding: 14 }}>
            <SectionTitle>Extracted Activity</SectionTitle>
            {ap && ap.confidence !== 'NONE' ? (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
                  <span style={{ fontSize: 11, color: T.textMuted }}>Confidence</span>
                  <Badge color={ap.confidence === 'HIGH' ? 'green' : ap.confidence === 'MEDIUM' ? 'amber' : 'red'}>{ap.confidence}</Badge>
                </div>
                {[['Turnover', fmtCcy(ap.monthlyTurnover)], ['Inbound', fmtCcy(ap.monthlyInboundValue)], ['Outbound', fmtCcy(ap.monthlyOutboundValue)], ['In Count', ap.monthlyInboundCount ?? '—'], ['Out Count', ap.monthlyOutboundCount ?? '—'], ['Schemes', ap.primarySchemes.join(', ') || '—']].map(([l, v]) =>
                  <div key={l} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, lineHeight: 2 }}>
                    <span style={{ color: T.textMuted }}>{l}</span>
                    <span style={{ color: T.text, fontWeight: 500 }}>{v}</span>
                  </div>
                )}
              </div>
            ) : (
              <div style={{ fontSize: 12, color: T.textMuted, textAlign: 'center', padding: 16 }}>
                Enter expected activity with £ amounts to see extracted profile.
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};


const ApprovedPanel = ({ customer, kyc, kycCase, refresh, currentUser }) => {
  const ex = kycCase.rmAssessment;
  const [verifying, setVerifying] = useState(false);
  const [snapshots, setSnapshots] = useState(null);
  const [loadingSnapshots, setLoadingSnapshots] = useState(false);
  const [apiError, setApiError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const loadSnapshots = async () => {
      if (!kyc.getCHSnapshots) return;
      setLoadingSnapshots(true);
      try {
        const snaps = await kyc.getCHSnapshots(customer.id);
        if (!cancelled) setSnapshots(Array.isArray(snaps) ? snaps : []);
      } catch (_) {
        if (!cancelled) setSnapshots([]);
      } finally {
        if (!cancelled) setLoadingSnapshots(false);
      }
    };
    loadSnapshots();
    return () => { cancelled = true; };
  }, [customer.id, kyc]);

  const handleVerifyCH = async () => {
    setVerifying(true);
    try {
      await kyc.verifyCH(customer.id);
      const snaps = await kyc.getCHSnapshots(customer.id);
      setSnapshots(Array.isArray(snaps) ? snaps : []);
      setApiError(null);
      await refresh();
    } catch (e) { setApiError(formatApiError(e)); }
    finally { setVerifying(false); }
  };

  // Calculate next review date based on risk rating (LOW=3yr, MEDIUM=2yr, HIGH=1yr)
  const approvalEntry = kycCase.statusHistory?.filter(h => h.status === KYCStatus.APPROVED).pop();
  const approvalDate = approvalEntry?.at;
  const reviewYears = kycCase.riskRating === 'HIGH' ? 1 : kycCase.riskRating === 'MEDIUM' ? 2 : 3;
  const nextReviewDate = approvalDate ? (() => {
    const d = new Date(approvalDate);
    d.setFullYear(d.getFullYear() + reviewYears);
    return d;
  })() : null;

  return (
    <div>
      {/* Green approval banner */}
      <div style={{ padding: '16px 24px', borderRadius: 8, background: T.greenBg, border: `1px solid ${T.greenBorder}`, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={{ fontSize: 24 }}>✓</span>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: T.green }}>
            Customer approved{ex?.rmId ? ` by ${ex.rmId}` : ''}{approvalDate ? ` on ${fmtDateUK(approvalDate)}` : ''}
          </div>
          {nextReviewDate && (
            <div style={{ fontSize: 12, color: T.textSecondary, marginTop: 2 }}>
              Next review due: {fmtDateUK(nextReviewDate.toISOString())}
            </div>
          )}
        </div>
        <div style={{ marginLeft: 'auto' }}>
          <RiskBadge rating={kycCase.riskRating} />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {/* Assessment summary */}
        <Card>
          <SectionTitle>Assessment Summary</SectionTitle>
          <div style={{ fontSize: 13, lineHeight: 1.8, color: T.textSecondary }}>
            {[
              ['Business', ex?.businessUnderstanding],
              ['Source of Wealth', ex?.sourceOfWealth],
              ['Expected Activity', ex?.expectedActivity],
              ['Concerns', ex?.concerns],
              ['Mitigants', ex?.mitigants],
              ['Recommendation', ex?.recommendation],
              ['Risk Rating', ex?.riskRating],
            ].map(([l, v]) => (
              <div key={l}><span style={{ color: T.textMuted, display: 'inline-block', width: 140 }}>{l}:</span> {v || '—'}</div>
            ))}
          </div>
        </Card>

        {/* Screening results summary */}
        <Card>
          <SectionTitle>Screening Results</SectionTitle>
          {kycCase.screeningResults.length === 0 ? (
            <div style={{ fontSize: 12, color: T.textMuted }}>No screening results recorded.</div>
          ) : (
            <div style={{ display: 'grid', gap: 10 }}>
              {Array.from(customer.individuals.values()).map(ind => {
                const ir = kycCase.screeningResults.filter(r => r.individualId === ind.id);
                return (
                  <div key={ind.id}>
                    <div style={{ fontSize: 13, fontWeight: 500, color: T.text, marginBottom: 6 }}>{ind.name}</div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
                      {ir.map((r, i) => (
                        <div key={i} style={{
                          padding: 8, borderRadius: 6,
                          background: r.result === 'CLEAR' ? T.greenBg : r.result === 'MATCH' ? T.redBg : T.amberBg,
                          border: `1px solid ${r.result === 'CLEAR' ? T.greenBorder : r.result === 'MATCH' ? T.redBorder : T.amberBorder}`,
                        }}>
                          <div style={{ fontSize: 10, fontWeight: 600, color: r.result === 'CLEAR' ? T.green : r.result === 'MATCH' ? T.red : T.amber, textTransform: 'uppercase', letterSpacing: 0.5 }}>{r.type.replace('_', ' ')}</div>
                          <div style={{ fontSize: 12, fontWeight: 500, color: r.result === 'CLEAR' ? T.green : r.result === 'MATCH' ? T.red : T.amber, marginTop: 3 }}>{r.result.replace('_', ' ')}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>

      <ErrorBanner error={apiError} onDismiss={() => setApiError(null)} onRetry={async () => { setApiError(null); try { await refresh(); } catch (e) { setApiError(formatApiError(e)); } }} />

      {/* Action buttons */}
      <div style={{ marginTop: 20, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <Btn variant="secondary" onClick={async () => { try { await kyc.triggerReview(customer.id, currentUser?.username || 'api'); setApiError(null); await refresh(); } catch (e) { setApiError(formatApiError(e)); } }}
          style={{ padding: '10px 24px', fontSize: 14 }}>
          Trigger Periodic Review
        </Btn>
        {customer.registrationNumber && !customer.registrationNumber.startsWith('DEMO') && (
          <Btn variant="secondary" onClick={handleVerifyCH} disabled={verifying}
            style={{ padding: '10px 24px', fontSize: 14 }}>
            {verifying ? 'Verifying...' : 'Verify with Companies House'}
          </Btn>
        )}
      </div>

      {/* Companies House snapshot history */}
      {snapshots && snapshots.length > 0 && (
        <Card style={{ marginTop: 16 }}>
          <SectionTitle>Companies House Snapshots</SectionTitle>
          {snapshots.map((snap, i) => (
            <div key={snap.id || i} style={{ padding: '8px 0', borderBottom: i < snapshots.length - 1 ? `1px solid ${T.borderLight}` : 'none', fontSize: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: T.text, fontWeight: 500 }}>{fmtDateTimeUK(snap.verified_at || snap.createdAt)}</span>
                <Badge color="green">Verified</Badge>
              </div>
              {snap.company_name && <div style={{ color: T.textSecondary, marginTop: 2 }}>{snap.company_name}</div>}
            </div>
          ))}
        </Card>
      )}
      {loadingSnapshots && <div style={{ fontSize: 12, color: T.textMuted, marginTop: 8 }}>Loading Companies House history...</div>}
    </div>
  );
};


const ReviewDuePanel = ({ customer, kyc, kycCase, refresh, currentUser }) => {
  const lastApproval = kycCase.statusHistory?.filter(h => h.status === KYCStatus.APPROVED).pop();
  const [apiError, setApiError] = useState(null);
  return (
    <div>
      <div style={{ padding: '16px 24px', borderRadius: 8, background: T.amberBg, border: `1px solid ${T.amberBorder}`, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={{ fontSize: 24 }}>⚠</span>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: T.amber }}>Periodic review due</div>
          {lastApproval && (
            <div style={{ fontSize: 12, color: T.textSecondary, marginTop: 2 }}>
              Last reviewed: {fmtDateUK(lastApproval.at)}
            </div>
          )}
        </div>
      </div>
      <ErrorBanner error={apiError} onDismiss={() => setApiError(null)} onRetry={async () => { setApiError(null); try { await refresh(); } catch (e) { setApiError(formatApiError(e)); } }} />
      <Btn onClick={async () => { try { await kyc.startReview(customer.id, currentUser?.username || 'api'); setApiError(null); await refresh(); } catch (e) { setApiError(formatApiError(e)); } }}
        style={{ padding: '12px 32px', fontSize: 15, fontWeight: 700 }}>
        Start Review
      </Btn>
    </div>
  );
};


const RejectedPanel = ({ customer, kycCase, kyc, refresh, currentUser }) => {
  const ex = kycCase.rmAssessment;
  const rejEntry = kycCase.statusHistory?.filter(h => h.status === KYCStatus.REJECTED).pop();
  const [reopening, setReopening] = useState(false);
  const [reopenError, setReopenError] = useState(null);
  return (
    <div>
      <div style={{ padding: '16px 24px', borderRadius: 8, background: T.redBg, border: `1px solid ${T.redBorder}`, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={{ fontSize: 24 }}>✗</span>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: T.red }}>
            Customer rejected{ex?.rmId ? ` by ${ex.rmId}` : ''}{rejEntry ? ` on ${fmtDateUK(rejEntry.at)}` : ''}
          </div>
          {ex?.concerns && (
            <div style={{ fontSize: 13, color: T.textSecondary, marginTop: 4 }}>
              Reason: {ex.concerns}
            </div>
          )}
        </div>
      </div>
      {/* Reopen case button */}
      <div style={{ marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10 }}>
        <Btn onClick={async () => {
          setReopening(true); setReopenError(null);
          try { await kyc.returnToDataCollection(customer.id, currentUser?.username || 'api', 'Case reopened for reconsideration'); await refresh(); }
          catch (e) { setReopenError(formatApiError(e)); }
          finally { setReopening(false); }
        }} disabled={reopening}
          variant="secondary"
          style={{ padding: '10px 20px', fontSize: 13, fontWeight: 600, background: T.amberBg, borderColor: T.amberBorder, color: T.amber }}>
          {reopening ? 'Reopening...' : 'Reopen Case'}
        </Btn>
        {reopenError && <span style={{ fontSize: 12, color: T.red }}>{reopenError}</span>}
      </div>
      {/* Read-only assessment */}
      {ex && (
        <Card>
          <SectionTitle>Assessment at Rejection</SectionTitle>
          <div style={{ fontSize: 13, lineHeight: 1.8, color: T.textSecondary }}>
            {[
              ['Business', ex.businessUnderstanding],
              ['Source of Wealth', ex.sourceOfWealth],
              ['Concerns', ex.concerns],
              ['Risk Rating', ex.riskRating],
              ['Recommendation', ex.recommendation],
            ].map(([l, v]) => (
              <div key={l}><span style={{ color: T.textMuted, display: 'inline-block', width: 140 }}>{l}:</span> {v || '—'}</div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};


// ============================================================
// CUSTOMER PIPELINE — List view of all KYC cases
// ============================================================

const CustomerPipeline = ({ kyc, monitoring, onSelect, onNew }) => {
  const [pipelineFilter, setPipelineFilter] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const cases = kyc.getAllCases();
  const stats = {
    total: cases.length,
    inProgress: cases.filter(c => c.status === KYCStatus.DATA_COLLECTION).length,
    review: cases.filter(c => c.status === KYCStatus.SCREENING || c.status === KYCStatus.RM_REVIEW).length,
    approved: cases.filter(c => c.status === KYCStatus.APPROVED).length,
  };
  const openAlerts = monitoring.getAllOpenAlerts().length;

  const alertsByCustomer = useMemo(() => {
    const m = new Map();
    for (const c of cases) {
      m.set(c.customerId, monitoring.getAlerts(c.customerId).filter(a => a.status === AlertStatus.OPEN));
    }
    return m;
  }, [cases, monitoring]);

  const filteredCases = cases.filter(c => {
    if (pipelineFilter === 'in_progress' && !(c.status === KYCStatus.DATA_COLLECTION || c.status === KYCStatus.SCREENING || c.status === KYCStatus.RM_REVIEW)) return false;
    if (pipelineFilter === 'screening_review' && !(c.status === KYCStatus.SCREENING || c.status === KYCStatus.RM_REVIEW)) return false;
    if (pipelineFilter === 'approved' && c.status !== KYCStatus.APPROVED) return false;
    if (pipelineFilter === 'open_alerts' && (alertsByCustomer.get(c.customerId) || []).length === 0) return false;
    if (searchTerm && !(c.customer?.legalName || '').toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  const toggleFilter = (key) => setPipelineFilter(prev => prev === key ? null : key);

  const statCardStyle = (key, color) => ({
    padding: 14, textAlign: 'center', cursor: 'pointer', transition: 'box-shadow 0.15s, border-color 0.15s',
    borderColor: pipelineFilter === key ? color : undefined,
    boxShadow: pipelineFilter === key ? `0 0 0 2px ${color}33` : undefined,
  });

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ fontSize: 18, fontWeight: 600, color: T.text, margin: 0, fontFamily: font }}>Customer Pipeline</h2>
        <Btn onClick={onNew}>+ New Customer</Btn>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: pipelineFilter ? 8 : 24 }}>
        <Card style={statCardStyle('in_progress', T.blue)} onClick={() => toggleFilter('in_progress')}><div style={{ fontSize: 24, fontWeight: 700, color: T.blue }}>{stats.inProgress}</div><div style={{ fontSize: 11, color: T.textMuted, marginTop: 2 }}>In Progress</div></Card>
        <Card style={statCardStyle('screening_review', T.purple)} onClick={() => toggleFilter('screening_review')}><div style={{ fontSize: 24, fontWeight: 700, color: T.purple }}>{stats.review}</div><div style={{ fontSize: 11, color: T.textMuted, marginTop: 2 }}>Screening / Review</div></Card>
        <Card style={statCardStyle('approved', T.green)} onClick={() => toggleFilter('approved')}><div style={{ fontSize: 24, fontWeight: 700, color: T.green }}>{stats.approved}</div><div style={{ fontSize: 11, color: T.textMuted, marginTop: 2 }}>Approved</div></Card>
        <Card style={statCardStyle('open_alerts', T.red)} onClick={() => toggleFilter('open_alerts')}><div style={{ fontSize: 24, fontWeight: 700, color: T.red }}>{openAlerts}</div><div style={{ fontSize: 11, color: T.textMuted, marginTop: 2 }}>Open Alerts</div></Card>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <input
          type="text"
          placeholder="Search customers..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          style={{ padding: '7px 12px', border: `1px solid ${T.border}`, borderRadius: 6, fontSize: 13, fontFamily: font, color: T.text, background: T.surface, outline: 'none', width: 240 }}
        />
        {(pipelineFilter || searchTerm) && (
          <div style={{ fontSize: 12, color: T.textSecondary, display: 'flex', alignItems: 'center', gap: 8 }}>
            Showing: {filteredCases.length} of {cases.length} customers
            <span onClick={() => { setPipelineFilter(null); setSearchTerm(''); }} style={{ color: T.blue, cursor: 'pointer', textDecoration: 'underline' }}>Clear filter</span>
          </div>
        )}
      </div>

      <Card style={{ padding: 0, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, fontFamily: font }}>
          <thead><tr style={{ background: T.surfaceAlt, borderBottom: `1px solid ${T.border}` }}>
            {['Customer', 'Reg. No.', 'Status', 'Risk', 'Alerts', 'RM'].map(h =>
              <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600, color: T.textSecondary, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>{h}</th>
            )}
          </tr></thead>
          <tbody>
            {filteredCases.map(c => {
              const al = alertsByCustomer.get(c.customerId) || [];
              const isDemo = isDemoData(c.customer);
              return (
                <tr key={c.customerId} onClick={() => onSelect(c.customerId)}
                  style={{ borderBottom: `1px solid ${T.borderLight}`, cursor: 'pointer' }}
                  onMouseEnter={e => e.currentTarget.style.background = T.surfaceAlt}
                  onMouseLeave={e => e.currentTarget.style.background = ''}>
                  <td style={{ padding: '12px 16px', fontWeight: 500, color: isDemo ? T.textMuted : T.text, fontStyle: isDemo ? 'italic' : 'normal' }}>{c.customer?.legalName}{isDemo && <span style={{ fontSize: 10, color: T.textMuted, marginLeft: 6 }}>(demo)</span>}</td>
                  <td style={{ padding: '12px 16px', color: T.textSecondary }}>{c.customer?.registrationNumber || '—'}</td>
                  <td style={{ padding: '12px 16px' }}><StatusBadge status={c.status} /></td>
                  <td style={{ padding: '12px 16px' }}><RiskBadge rating={c.riskRating} /></td>
                  <td style={{ padding: '12px 16px' }}>{al.length > 0 ? <Badge color="red">{al.length} open</Badge> : <span style={{ color: T.textMuted }}>—</span>}</td>
                  <td style={{ padding: '12px 16px', color: T.textSecondary }}>{c.rmAssessment?.rmId || '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>
    </div>
  );
};


// FSCS panel extracted to src/modules/FSCSPanel.jsx
// FSCSTab alias preserved for backwards compatibility
const FSCSTab = FSCSPanel;


// ============================================================
// CUSTOMER DETAIL — Stepper + Action Panel + Tabs
// ============================================================

const CustomerDetail = ({ kyc, monitoring, runQualityGate, extractActivityProfile, customerId, onBack, refresh, currentUser }) => {
  const [tab, setTab] = useState(null); // null = show action panel (default)
  const customer = kyc.getCustomer(customerId);
  const kycCase = kyc.getKYCCase(customerId);

  // Lift assessment draft state here so it survives tab switches
  const ex = kycCase?.rmAssessment;
  const [assessmentDraft, setAssessmentDraft] = useState({
    rmId: ex?.rmId || currentUser?.username || 'rm_user',
    businessUnderstanding: ex?.businessUnderstanding || '',
    sourceOfWealth: ex?.sourceOfWealth || '',
    expectedActivity: ex?.expectedActivity || '',
    concerns: ex?.concerns || '',
    mitigants: ex?.mitigants || '',
    recommendation: ex?.recommendation || 'APPROVE',
    riskRating: ex?.riskRating || 'LOW',
  });

  if (!customer || !kycCase) return <div>Not found</div>;

  const detailTabs = [
    { key: null, label: 'Workflow' },
    { key: 'individuals', label: `People (${customer.individuals.size})` },
    { key: 'screening', label: 'Screening' },
    { key: 'monitoring', label: 'Monitoring' },
    { key: 'fscs', label: 'FSCS' },
    { key: 'timeline', label: 'Timeline' },
  ];

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <Btn variant="ghost" onClick={onBack} small>← Back</Btn>
        <h2 style={{ fontSize: 18, fontWeight: 600, color: T.text, margin: 0, fontFamily: font }}>{customer.legalName}</h2>
        <StatusBadge status={kycCase.status} />
        <RiskBadge rating={kycCase.riskRating} />
      </div>

      {/* Stepper bar */}
      <StepperBar status={kycCase.status} />

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: `1px solid ${T.border}` }}>
        {detailTabs.map(t => (
          <button key={t.key || 'workflow'} onClick={() => setTab(t.key)} style={{
            padding: '8px 16px', fontSize: 13, fontWeight: tab === t.key ? 600 : 400,
            color: tab === t.key ? T.blue : T.textSecondary, background: 'transparent', border: 'none',
            borderBottom: tab === t.key ? `2px solid ${T.blue}` : '2px solid transparent',
            cursor: 'pointer', fontFamily: font, marginBottom: -1,
          }}>{t.label}</button>
        ))}
      </div>

      {/* Action panel (default tab) */}
      {tab === null && kycCase.status === KYCStatus.DATA_COLLECTION && (
        <DataCollectionPanel customer={customer} kyc={kyc} kycCase={kycCase} refresh={refresh} currentUser={currentUser} />
      )}
      {tab === null && kycCase.status === KYCStatus.SCREENING && (
        <ScreeningPanel customer={customer} kyc={kyc} kycCase={kycCase} refresh={refresh} currentUser={currentUser} />
      )}
      {tab === null && kycCase.status === KYCStatus.RM_REVIEW && (
        <RMReviewPanel customer={customer} kyc={kyc} kycCase={kycCase} monitoring={monitoring}
          runQualityGate={runQualityGate} extractActivityProfile={extractActivityProfile}
          refresh={refresh} draft={assessmentDraft} setDraft={setAssessmentDraft} isPeriodicReview={false} currentUser={currentUser} />
      )}
      {tab === null && kycCase.status === KYCStatus.APPROVED && (
        <ApprovedPanel customer={customer} kyc={kyc} kycCase={kycCase} refresh={refresh} currentUser={currentUser} />
      )}
      {tab === null && kycCase.status === KYCStatus.REVIEW_DUE && (
        <ReviewDuePanel customer={customer} kyc={kyc} kycCase={kycCase} refresh={refresh} currentUser={currentUser} />
      )}
      {tab === null && kycCase.status === KYCStatus.UNDER_REVIEW && (
        <RMReviewPanel customer={customer} kyc={kyc} kycCase={kycCase} monitoring={monitoring}
          runQualityGate={runQualityGate} extractActivityProfile={extractActivityProfile}
          refresh={refresh} draft={assessmentDraft} setDraft={setAssessmentDraft} isPeriodicReview={true} currentUser={currentUser} />
      )}
      {tab === null && kycCase.status === KYCStatus.REJECTED && (
        <RejectedPanel customer={customer} kycCase={kycCase} kyc={kyc} refresh={refresh} currentUser={currentUser} />
      )}

      {/* Detail tabs */}
      {tab === 'individuals' && <IndividualsTab customer={customer} kyc={kyc} kycCase={kycCase} refresh={refresh} />}
      {tab === 'screening' && <ScreeningTab customer={customer} kyc={kyc} kycCase={kycCase} refresh={refresh} />}
      {tab === 'monitoring' && <MonitoringTab customer={customer} monitoring={monitoring} />}
      {tab === 'fscs' && <FSCSPanel customerId={customerId} currentUser={currentUser} />}
      {tab === 'timeline' && <TimelineTab kycCase={kycCase} />}
    </div>
  );
};


// ============================================================
// DETAIL TABS (preserved from original)
// ============================================================

const IndividualsTab = ({ customer, kyc, kycCase, refresh }) => {
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [apiError, setApiError] = useState(null);
  const [form, setForm] = useState({ name: '', dateOfBirth: '', nationality: '', role: 'DIRECTOR', ownershipPct: 0, sourceOfWealth: '' });
  const [editForm, setEditForm] = useState({});
  const inds = Array.from(customer.individuals.values());
  const isDemo = isDemoData(customer);
  const canEdit = kycCase.status === KYCStatus.DATA_COLLECTION;

  const handleAdd = async () => {
    try {
      await kyc.addIndividual(customer.id, {
        name: form.name,
        dateOfBirth: form.dateOfBirth,
        nationality: form.nationality,
        role: form.role,
        ownershipPct: Number(form.ownershipPct) || 0,
        sourceOfWealth: form.sourceOfWealth || undefined,
      });
      setForm({ name: '', dateOfBirth: '', nationality: '', role: 'DIRECTOR', ownershipPct: 0, sourceOfWealth: '' });
      setAdding(false);
      setApiError(null);
      await refresh();
    } catch (e) { setApiError(formatApiError(e)); }
  };

  const startEdit = (ind) => {
    setEditingId(ind.id);
    setEditForm({
      name: ind.name || '', dateOfBirth: ind.dateOfBirth || '', nationality: ind.nationality || '',
      role: ind.role || 'DIRECTOR', ownershipPct: ind.ownershipPct || 0, sourceOfWealth: ind.sourceOfWealth || '',
    });
  };

  const handleSaveEdit = async () => {
    try {
      await kyc.updateIndividual(customer.id, editingId, {
        name: editForm.name,
        dateOfBirth: editForm.dateOfBirth,
        nationality: editForm.nationality,
        role: editForm.role,
        ownershipPct: Number(editForm.ownershipPct) || 0,
        sourceOfWealth: editForm.sourceOfWealth || undefined,
      });
      setEditingId(null);
      setApiError(null);
      await refresh();
    } catch (e) { setApiError(formatApiError(e)); }
  };

  return (
    <div>
      <ErrorBanner error={apiError} onDismiss={() => setApiError(null)} onRetry={async () => { setApiError(null); try { await refresh(); } catch (e) { setApiError(formatApiError(e)); } }} />
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <SectionTitle>Directors, UBOs & Signatories ({inds.length})</SectionTitle>
        {canEdit && <Btn small onClick={() => setAdding(!adding)}>{adding ? 'Cancel' : '+ Add Person'}</Btn>}
      </div>

      {adding && (
        <Card style={{ marginBottom: 16, background: T.surfaceAlt }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Input label="Full Name" value={form.name} onChange={v => setForm(f => ({ ...f, name: v }))} placeholder="e.g. John Smith" />
            <Input label="Date of Birth (DD/MM/YYYY)" value={form.dateOfBirth} onChange={v => setForm(f => ({ ...f, dateOfBirth: v }))} placeholder="e.g. 15/01/1980" />
            <CountrySelect label="Nationality" value={form.nationality} onChange={v => setForm(f => ({ ...f, nationality: v }))} placeholder="Search country e.g. United Kingdom" />
            <Select label="Role" value={form.role} onChange={v => setForm(f => ({ ...f, role: v }))} options={[
              { value: 'DIRECTOR', label: 'Director' }, { value: 'UBO', label: 'UBO (25%+)' },
              { value: 'DIRECTOR_UBO', label: 'Director & UBO' }, { value: 'SIGNATORY', label: 'Signatory' },
            ]} />
            <Input label="Ownership %" value={form.ownershipPct} onChange={v => setForm(f => ({ ...f, ownershipPct: v }))} placeholder="e.g. 25" />
            <Input label="Source of Wealth" value={form.sourceOfWealth} onChange={v => setForm(f => ({ ...f, sourceOfWealth: v }))} placeholder="e.g. Professional income" />
          </div>
          <div style={{ marginTop: 12 }}><Btn onClick={handleAdd}>Add Person</Btn></div>
        </Card>
      )}

      {inds.length === 0 && !adding && (
        <Card style={{ textAlign: 'center', padding: 30, color: T.textMuted, fontSize: 13 }}>
          No individuals (directors/UBOs) recorded yet.
          {canEdit && <div style={{ marginTop: 8 }}><Btn small onClick={() => setAdding(true)}>+ Add Person</Btn></div>}
        </Card>
      )}

      {inds.map(ind => {
        const isEditing = editingId === ind.id;
        return (
          <Card key={ind.id} style={{ marginBottom: 8, padding: 14, ...(isDemo ? { borderLeft: `3px solid ${T.border}` } : {}) }}>
            {isEditing ? (
              <div>
                {(() => {
                  const othersTotal = inds.filter(i => i.id !== ind.id).reduce((sum, i) => sum + (i.ownershipPct || 0), 0);
                  const editPct = parseInt(editForm.ownershipPct) || 0;
                  const editTotal = othersTotal + editPct;
                  const available = 100 - othersTotal;
                  return (
                    <div style={{ marginBottom: 10, padding: '6px 10px', borderRadius: 6, background: T.bg, border: `1px solid ${T.border}`, fontSize: 12, display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: T.textMuted }}>Others: {othersTotal}% — <span style={{ color: available > 0 ? T.green : T.red, fontWeight: 600 }}>{available}% available</span></span>
                      {editTotal > 100 && <span style={{ color: T.red, fontWeight: 600 }}>Total {editTotal}% exceeds 100%</span>}
                    </div>
                  );
                })()}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <Input label="Full Name" value={editForm.name} onChange={v => setEditForm(f => ({ ...f, name: v }))} placeholder="e.g. John Smith" />
                  <Input label="Date of Birth (DD/MM/YYYY)" value={editForm.dateOfBirth} onChange={v => setEditForm(f => ({ ...f, dateOfBirth: v }))} placeholder="e.g. 15/01/1980" />
                  <CountrySelect label="Nationality" value={editForm.nationality} onChange={v => setEditForm(f => ({ ...f, nationality: v }))} placeholder="Search country e.g. United Kingdom" />
                  <Select label="Role" value={editForm.role} onChange={v => setEditForm(f => ({ ...f, role: v }))} options={[
                    { value: 'DIRECTOR', label: 'Director' }, { value: 'UBO', label: 'UBO (25%+)' },
                    { value: 'DIRECTOR_UBO', label: 'Director & UBO' }, { value: 'SIGNATORY', label: 'Signatory' },
                  ]} />
                  <Input label="Ownership %" value={editForm.ownershipPct} onChange={v => setEditForm(f => ({ ...f, ownershipPct: v }))} />
                  <Input label="Source of Wealth" value={editForm.sourceOfWealth} onChange={v => setEditForm(f => ({ ...f, sourceOfWealth: v }))} />
                </div>
                <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
                  <Btn small onClick={handleSaveEdit}>Save</Btn>
                  <Btn small variant="ghost" onClick={() => setEditingId(null)}>Cancel</Btn>
                </div>
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <div>
                    <DemoValue isDemo={isDemo} value={ind.name}><span style={{ fontSize: 14, fontWeight: 500, color: isDemo ? T.textMuted : T.text, fontStyle: isDemo ? 'italic' : 'normal' }}>{ind.name}</span></DemoValue>
                    <div style={{ fontSize: 12, color: T.textMuted }}>{ind.role.replace('_', ' & ')}{ind.ownershipPct > 0 ? ` · ${ind.ownershipPct}%` : ''}</div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <SanctionedBadge nationality={ind.nationality} />
                    {ind.screeningResults && ind.screeningResults.length > 0
                      ? (ind.screeningResults.some(r => r.result === 'MATCH')
                        ? <Badge color="red">Match</Badge>
                        : ind.screeningResults.some(r => r.result === 'POSSIBLE_MATCH')
                          ? <Badge color="amber">Possible</Badge>
                          : <Badge color="green">Clear</Badge>)
                      : <Badge color="gray">Not screened</Badge>}
                    {canEdit && <Btn small variant="ghost" onClick={() => startEdit(ind)}>Edit</Btn>}
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginTop: 8, fontSize: 11 }}>
                  {[['DOB', fmtDateUK(ind.dateOfBirth)], ['Nationality', ind.nationality ? `${ind.nationality} — ${getCountryName(ind.nationality)}` : '—'], ['Source of Wealth', ind.sourceOfWealth || '—'], ['ID Verified', ind.idVerificationStatus || 'Pending']].map(([l, v]) =>
                    <div key={l}>
                      <span style={{ color: T.textMuted, display: 'block' }}>{l}</span>
                      <DemoValue isDemo={isDemo} value={v}><span style={{ color: T.textSecondary }}>{v}</span></DemoValue>
                      {!v || v === '—' ? <span style={{ fontSize: 10, color: T.red }}>Missing</span> : null}
                    </div>
                  )}
                </div>
              </>
            )}
          </Card>
        );
      })}
    </div>
  );
};

const ScreeningTab = ({ customer, kyc, kycCase, refresh }) => {
  const [screening, setScreening] = useState(false);
  const [screenError, setScreenError] = useState(null);
  const results = kycCase.screeningResults;
  const inds = Array.from(customer.individuals.values());
  const provider = kycCase.screeningProvider || 'simulated';

  const handleRunScreening = async () => {
    setScreening(true);
    setScreenError(null);
    try {
      await kyc.runScreening(customer.id);
      await refresh();
    } catch (e) {
      setScreenError(e.message);
    } finally {
      setScreening(false);
    }
  };

  if (results.length === 0) {
    return (
      <Card style={{ textAlign: 'center', padding: 40, color: T.textMuted }}>
        {screening
          ? <div style={{ fontSize: 13 }}>Screening in progress...</div>
          : <div style={{ fontSize: 13 }}>{kycCase.status === KYCStatus.SCREENING ? 'Ready to run.' : 'Not available at this stage.'}</div>}
        {screenError && <div style={{ fontSize: 12, color: T.red, marginTop: 8 }}>{screenError}</div>}
        {kycCase.status === KYCStatus.SCREENING && !screening && <Btn onClick={handleRunScreening} style={{ marginTop: 12 }}>Run Screening</Btn>}
      </Card>
    );
  }

  return (
    <div>
      <Card style={{ marginBottom: 12, padding: 10, display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ fontSize: 11, color: T.textMuted }}>Provider:</div>
        <div style={{ fontSize: 12, fontWeight: 600, color: provider === 'griffin' ? T.purple : T.textSecondary }}>
          {provider === 'griffin' ? 'Griffin Verify' : 'Simulated'}
        </div>
        {kycCase.griffinVerificationUrl && (
          <>
            <div style={{ fontSize: 11, color: T.textMuted, marginLeft: 12 }}>Verification:</div>
            <div style={{ fontSize: 11, color: T.blue, fontFamily: 'monospace' }}>{kycCase.griffinVerificationUrl}</div>
          </>
        )}
      </Card>
      {inds.map(ind => {
        const ir = results.filter(r => r.individualId === ind.id);
        return (
          <Card key={ind.id} style={{ marginBottom: 12, padding: 14 }}>
            <div style={{ fontSize: 14, fontWeight: 500, color: T.text, marginBottom: 10 }}>
              {ind.name} <span style={{ fontSize: 11, color: T.textMuted }}>({ind.role.replace('_', ' & ')})</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
              {ir.map((r, i) => (
                <div key={i} style={{
                  padding: 10, borderRadius: 6,
                  background: r.result === 'CLEAR' ? T.greenBg : r.result === 'MATCH' ? T.redBg : T.amberBg,
                  border: `1px solid ${r.result === 'CLEAR' ? T.greenBorder : r.result === 'MATCH' ? T.redBorder : T.amberBorder}`,
                }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: r.result === 'CLEAR' ? T.green : r.result === 'MATCH' ? T.red : T.amber, textTransform: 'uppercase', letterSpacing: 0.5 }}>{r.type}</div>
                  <div style={{ fontSize: 13, fontWeight: 500, color: r.result === 'CLEAR' ? T.green : r.result === 'MATCH' ? T.red : T.amber, marginTop: 4 }}>{r.result.replace('_', ' ')}</div>
                  {r.details && <div style={{ fontSize: 11, color: T.textSecondary, marginTop: 4 }}>{r.details}</div>}
                  {r.source === 'griffin' && <div style={{ fontSize: 10, color: T.textMuted, marginTop: 2 }}>via Griffin</div>}
                </div>
              ))}
            </div>
          </Card>
        );
      })}
    </div>
  );
};

const MonitoringTab = ({ customer, monitoring }) => {
  const summary = monitoring.getMonitoringSummary(customer.id);
  const alerts = monitoring.getAlerts(customer.id);
  const [selAlert, setSelAlert] = useState(null);
  const [notes, setNotes] = useState('');
  const [, forceUpdate] = useState(0);

  const handleReview = async (id, status) => {
    await monitoring.reviewAlert(id, { reviewedBy: 'rm_user', status, notes });
    setSelAlert(null);
    setNotes('');
    forceUpdate(n => n + 1);
  };

  const open = alerts.filter(a => a.status === AlertStatus.OPEN || a.status === AlertStatus.UNDER_REVIEW);
  const closed = alerts.filter(a => a.status !== AlertStatus.OPEN && a.status !== AlertStatus.UNDER_REVIEW);

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
        <Card style={{ padding: 12, textAlign: 'center' }}><div style={{ fontSize: 20, fontWeight: 700, color: T.blue }}>{summary.totalTransactions}</div><div style={{ fontSize: 11, color: T.textMuted }}>Transactions</div></Card>
        <Card style={{ padding: 12, textAlign: 'center' }}><div style={{ fontSize: 20, fontWeight: 700, color: T.red }}>{summary.alertCounts.open}</div><div style={{ fontSize: 11, color: T.textMuted }}>Open Alerts</div></Card>
        <Card style={{ padding: 12, textAlign: 'center' }}><div style={{ fontSize: 20, fontWeight: 700, color: T.amber }}>{summary.alertCounts.escalated}</div><div style={{ fontSize: 11, color: T.textMuted }}>Escalated</div></Card>
        <Card style={{ padding: 12, textAlign: 'center' }}><div style={{ fontSize: 20, fontWeight: 700, color: T.green }}>{summary.alertCounts.total - summary.alertCounts.open}</div><div style={{ fontSize: 11, color: T.textMuted }}>Resolved</div></Card>
      </div>

      {summary.profile && (
        <Card style={{ marginBottom: 16, padding: 14 }}>
          <SectionTitle>Expected Profile</SectionTitle>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, fontSize: 12 }}>
            <div><span style={{ color: T.textMuted, display: 'block' }}>Monthly In</span><span style={{ color: T.text, fontWeight: 600, fontSize: 14 }}>£{(summary.profile.monthlyInboundValue || 0).toLocaleString()}</span></div>
            <div><span style={{ color: T.textMuted, display: 'block' }}>Monthly Out</span><span style={{ color: T.text, fontWeight: 600, fontSize: 14 }}>£{(summary.profile.monthlyOutboundValue || 0).toLocaleString()}</span></div>
            <div><span style={{ color: T.textMuted, display: 'block' }}>Risk</span><RiskBadge rating={summary.profile.riskRating} /></div>
          </div>
        </Card>
      )}

      <SectionTitle>Open Alerts ({open.length})</SectionTitle>
      {open.length === 0
        ? <Card style={{ textAlign: 'center', padding: 30, color: T.textMuted, fontSize: 13 }}>No open alerts</Card>
        : open.map(a => (
          <Card key={a.id} style={{ marginBottom: 8, padding: 14, borderLeft: `3px solid ${a.severity === 'HIGH' || a.severity === 'CRITICAL' ? T.red : a.severity === 'MEDIUM' ? T.amber : T.blue}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{a.type.replace(/_/g, ' ')}</span>
                  <Badge color={a.severity === 'HIGH' || a.severity === 'CRITICAL' ? 'red' : 'amber'}>{a.severity}</Badge>
                </div>
                <div style={{ fontSize: 12, color: T.textSecondary, marginTop: 4 }}>
                  {a.details?.metric
                    ? `Expected: £${a.details.expected?.toLocaleString()} · Actual: £${a.details.actual?.toLocaleString()} · ${a.details.deviationPct}%`
                    : a.details?.amount
                      ? `£${a.details.amount?.toLocaleString()} (${a.details.pctOfMonthly}% of monthly)`
                      : ''}
                </div>
              </div>
              {selAlert === a.id ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 200 }}>
                  <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Notes..." rows={2} style={{ padding: 6, borderRadius: 4, border: `1px solid ${T.border}`, fontSize: 11, fontFamily: font }} />
                  <div style={{ display: 'flex', gap: 4 }}>
                    <Btn small onClick={() => handleReview(a.id, AlertStatus.CLOSED_FALSE_POSITIVE)}>False Positive</Btn>
                    <Btn small variant="danger" onClick={() => handleReview(a.id, AlertStatus.ESCALATED)}>Escalate</Btn>
                    <Btn small variant="ghost" onClick={() => setSelAlert(null)}>Cancel</Btn>
                  </div>
                </div>
              ) : (
                <Btn small variant="secondary" onClick={() => setSelAlert(a.id)}>Review</Btn>
              )}
            </div>
          </Card>
        ))}

      {closed.length > 0 && (
        <>
          <SectionTitle>Resolved ({closed.length})</SectionTitle>
          {closed.map(a => (
            <Card key={a.id} style={{ marginBottom: 6, padding: 10, opacity: 0.7 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                <span style={{ color: T.textSecondary }}>{a.type.replace(/_/g, ' ')}</span>
                <Badge color={a.status === AlertStatus.ESCALATED ? 'red' : 'gray'}>{a.status.replace(/_/g, ' ')}</Badge>
              </div>
            </Card>
          ))}
        </>
      )}

      {!summary.profile && (
        <Card style={{ textAlign: 'center', padding: 40, color: T.textMuted, fontSize: 13, marginTop: 16 }}>
          No monitoring profile. Complete RM assessment with expected activity to enable.
        </Card>
      )}
    </div>
  );
};

const TimelineTab = ({ kycCase }) => (
  <div>
    <SectionTitle>Status History</SectionTitle>
    <Card style={{ padding: 0 }}>
      {kycCase.statusHistory.map((h, i) => (
        <div key={i} style={{ display: 'flex', gap: 16, padding: '12px 16px', borderBottom: i < kycCase.statusHistory.length - 1 ? `1px solid ${T.borderLight}` : 'none' }}>
          <div style={{ width: 80, fontSize: 11, color: T.textMuted }}>
            {fmtDateUK(h.at)}<br />
            {new Date(h.at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
          </div>
          <div style={{ width: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: i === kycCase.statusHistory.length - 1 ? T.blue : T.border, marginTop: 4 }} />
            {i < kycCase.statusHistory.length - 1 && <div style={{ width: 1, flex: 1, background: T.border, marginTop: 4 }} />}
          </div>
          <div>
            <StatusBadge status={h.status} />
            <div style={{ fontSize: 12, color: T.textSecondary, marginTop: 4 }}>{h.by && `by ${h.by}`}{h.reason ? ` — ${h.reason}` : ''}</div>
          </div>
        </div>
      ))}
    </Card>
  </div>
);


// ============================================================
// NEW CUSTOMER FORM
// ============================================================

const NewCustomerForm = ({ kyc, onComplete, onCancel }) => {
  const [form, setForm] = useState({ type: 'CORPORATE', legalName: '', registrationNumber: '', registeredAddress: '', sicCodes: '', incorporationDate: '' });
  const [creating, setCreating] = useState(false);
  const [apiError, setApiError] = useState(null);
  const create = async () => {
    if (!form.legalName.trim()) { setApiError('Legal name required'); return; }
    setCreating(true);
    setApiError(null);
    try {
      const result = await kyc.createCustomer({ ...form, sicCodes: form.sicCodes ? form.sicCodes.split(',').map(s => s.trim()) : [] });
      const customer = result.customer || result;
      if (!customer?.id) { setApiError('Failed to create customer: no ID returned'); setCreating(false); return; }
      onComplete(customer.id);
    } catch (err) {
      setApiError(formatApiError(err));
      setCreating(false);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <Btn variant="ghost" onClick={onCancel} small>← Back</Btn>
        <h2 style={{ fontSize: 18, fontWeight: 600, color: T.text, margin: 0, fontFamily: font }}>New Customer Onboarding</h2>
      </div>
      <ErrorBanner error={apiError} onDismiss={() => setApiError(null)} />
      <Card style={{ maxWidth: 600 }}>
        <Select label="Type" value={form.type} onChange={v => setForm(f => ({ ...f, type: v }))} options={[{ value: 'CORPORATE', label: 'Corporate' }, { value: 'SOLE_TRADER', label: 'Sole Trader' }, { value: 'PARTNERSHIP', label: 'Partnership' }, { value: 'TRUST', label: 'Trust' }]} />
        <Input label="Legal Name *" value={form.legalName} onChange={v => setForm(f => ({ ...f, legalName: v }))} placeholder="e.g. Acme Corp Ltd" />
        <Input label="Registration No." value={form.registrationNumber} onChange={v => setForm(f => ({ ...f, registrationNumber: v }))} placeholder="e.g. 12345678" />
        <Input label="Registered Address" value={form.registeredAddress} onChange={v => setForm(f => ({ ...f, registeredAddress: v }))} placeholder="e.g. 1 Main Street, London" />
        <Input label="SIC Codes" value={form.sicCodes} onChange={v => setForm(f => ({ ...f, sicCodes: v }))} placeholder="e.g. 62020, 46900" />
        <Input label="Incorporation Date (DD/MM/YYYY)" value={form.incorporationDate} onChange={v => setForm(f => ({ ...f, incorporationDate: v }))} placeholder="e.g. 15/01/2020" />
        <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
          <Btn onClick={create} disabled={creating}>{creating ? 'Creating…' : 'Create & Start Onboarding'}</Btn>
          <Btn variant="secondary" onClick={onCancel}>Cancel</Btn>
        </div>
      </Card>
    </div>
  );
};


// ============================================================
// KYC MODULE — Main exported component
// ============================================================

export { StepperBar, DataCollectionPanel, ScreeningPanel, RMReviewPanel, ApprovedPanel, ReviewDuePanel, RejectedPanel, FSCSTab };

export default function KYCModule({ kyc, monitoring, runQualityGate, extractActivityProfile, refresh }) {
  const [kycView, setKycView] = useState('pipeline');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    api.get('/me').then(user => setCurrentUser(user)).catch(() => {});
  }, []);

  return (
    <div>
      {kycView === 'pipeline' && <CustomerPipeline kyc={kyc} monitoring={monitoring} onSelect={id => { setSelectedCustomer(id); setKycView('detail'); }} onNew={() => setKycView('new')} />}
      {kycView === 'detail' && selectedCustomer && <CustomerDetail kyc={kyc} monitoring={monitoring} runQualityGate={runQualityGate} extractActivityProfile={extractActivityProfile} customerId={selectedCustomer} onBack={() => { setKycView('pipeline'); setSelectedCustomer(null); }} refresh={refresh} currentUser={currentUser} />}
      {kycView === 'new' && <NewCustomerForm kyc={kyc} onComplete={id => { setSelectedCustomer(id); setKycView('detail'); refresh(); }} onCancel={() => setKycView('pipeline')} />}
    </div>
  );
}
